import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { OneValueMeasurement } from "~/api/lastMeasurements";
import { BodyWeight } from "~/icons";
import { Button } from "~/ui";
import { formatBackendDate, formatDecimal2 } from "~/utils";
import { HealthDetails } from "..";
import { BodyWeightInputModal } from "./BodyWeightInputModal";

interface BodyWeightProps {
  measurement: OneValueMeasurement | null;
  patientId: number;
}

export const BodyWeightCard = ({ measurement, patientId }: BodyWeightProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewMore = () => {
    void navigate(`/airmen/${patientId}/body-weight`);
  };

  const handleAddMeasurement = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setIsModalOpen(true);
  };

  // Collapsed card view
  return (
    <>
      <HealthDetails.Card
        onClick={handleViewMore}
        className="cursor-pointer transition-shadow duration-200 hover:shadow-lg"
      >
        <HealthDetails.CardHeader className="justify-between">
          <div className="flex items-center gap-2">
            <BodyWeight className="text-primary-40 dark:text-primary-80" />
            <HealthDetails.CardTitle>
              {t("health.body_weight")}
            </HealthDetails.CardTitle>{" "}
          </div>

          <Button
            text="Log data"
            color="primary"
            onClick={handleAddMeasurement}
            className="px-3 py-0.5 [&_span]:text-sm"
          />
        </HealthDetails.CardHeader>
        <HealthDetails.CardBody>
          <HealthDetails.CardResult>
            {measurement
              ? formatDecimal2(measurement.value)
              : t("health.no_data")}
          </HealthDetails.CardResult>
          <HealthDetails.CardDetails>
            <HealthDetails.CardUnit>
              {t("health.pounds")}
            </HealthDetails.CardUnit>
            {measurement && (
              <HealthDetails.CardDate>
                {formatBackendDate(measurement.date)}
              </HealthDetails.CardDate>
            )}
          </HealthDetails.CardDetails>
          <div className="flex flex-1 items-center justify-end">
            <button className="text-sm text-primary-40 transition-colors hover:text-primary-30 dark:text-primary-80 dark:hover:text-primary-90">
              {t("general.view_more")}
            </button>
          </div>
        </HealthDetails.CardBody>
      </HealthDetails.Card>

      <BodyWeightInputModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        patientId={patientId}
      />
    </>
  );
};
